import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { F, Button, Flex, Field, Divider } from 'nallian-shared-ui/lib/components';
import {
  SelectFieldValueComponent,
  InputFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import ReadOnlyFieldValueComponent from '../../../../../mgmt/app/components/ReadOnlyFieldValueComponent';

const EMPTY_FIELD = { value: '' };

const FieldFlex = styled(Flex)`
  && .field {
    width: 100%;
    margin: 0;
  }
`;

const FieldLabel = styled.label`
  margin: 0 0 0.28571429rem 0;
  color: rgba(0, 0, 0, 0.87);
  font-size: 0.92857143em;
  font-weight: bold;
`;

const IdentificationCodeFields = ({
  fields,
  submitProcessing,
  identificationCodeTypes,
  hideTitle,
  prefillRequiredOptions,
}) => {
  const { t } = useTranslation();

  const handleAddFieldClick = useCallback(() => fields.push(EMPTY_FIELD), [fields]);
  const handleRemoveFieldClick = useCallback((_, { i }) => fields.remove(i), [fields]);

  useEffect(() => {
    if (prefillRequiredOptions) {
      const allFields = fields.getAll() || [];

      identificationCodeTypes
        .filter(
          ct => ct.isRequired && !allFields.find(f => f.identificationCodeTypeTag === ct.value)
        )
        .forEach(ct =>
          fields.push({ identificationCodeTypeTag: ct.value, value: '', isRequired: true })
        );
    }
  }, [prefillRequiredOptions, identificationCodeTypes, fields]);

  return (
    <F.Field error={false}>
      <Flex fluid direction="column" margin={!hideTitle && '-1rem 0 0 0'}>
        {!hideTitle && (
          <>
            <FieldLabel htmlFor="-">{t('identificationCodes')}</FieldLabel>
            <Divider />
          </>
        )}
        <FieldFlex fluid direction="column" alignItems="flex-start">
          {fields.map((f, index) => {
            const field = fields.get(index);
            return (
              <Flex key={`idcode_${index}`} fluid alignItems="center">
                <Field
                  fluid
                  search
                  clearable
                  label={index === 0 && t('type')}
                  name={`${f}.identificationCodeTypeTag`}
                  options={identificationCodeTypes}
                  valueComponent={
                    field.isRequired ? ReadOnlyFieldValueComponent : SelectFieldValueComponent
                  }
                  disabled={submitProcessing}
                />
                <Flex margin="0 0.5rem" />
                <Field
                  fluid
                  search
                  clearable
                  label={index === 0 && t('code')}
                  name={`${f}.value`}
                  valueComponent={InputFieldValueComponent}
                  disabled={submitProcessing}
                />
                <Flex margin="0 0.5rem" />
                <Flex
                  direction="column"
                  alignItems="flex-start"
                  margin={index === 0 ? '1.5rem 0.75rem 0' : '0 0.75rem 0'}
                >
                  <Button
                    iconbutton
                    linkbutton
                    disabled={field.isRequired}
                    icon="close"
                    type="button"
                    tabindex="-1"
                    i={index}
                    onClick={handleRemoveFieldClick}
                  />
                </Flex>
              </Flex>
            );
          })}
          <Flex fluid justifyContent={hideTitle ? 'flex-start' : 'flex-end'} margin="1rem 0 0 0">
            <Button
              linkbutton
              icon="add"
              type="button"
              tabindex="-1"
              content={t('addIdentificationCode')}
              onClick={handleAddFieldClick}
            />
          </Flex>
        </FieldFlex>
      </Flex>
    </F.Field>
  );
};

IdentificationCodeFields.propTypes = {
  fields: PropTypes.array.isRequired,
  submitProcessing: PropTypes.bool.isRequired,
  identificationCodeTypes: PropTypes.array,
  hideTitle: PropTypes.bool,
  prefillRequiredOptions: PropTypes.bool,
};

IdentificationCodeFields.defaultProps = {
  identificationCodeTypes: [],
  prefillRequiredOptions: false,
  hideTitle: false,
};

export default IdentificationCodeFields;
